import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart3, TrendingUp, Users, Download, Euro, Clock,
  Activity, FileText, ChevronRight, AlertCircle, CheckCircle,
  Lightbulb, Calendar, DollarSign, UserPlus, FileSpreadsheet,
  PieChart, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { reportsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';
import BarChart from '../components/charts/BarChart';
import DonutChart from '../components/charts/DonutChart';
import './Reports.css';

// ── Types ──────────────────────────────────────────────────────

interface FinancialsData {
  period: { from_date: string; to_date: string };
  revenue_net: number;
  revenue_gross: number;
  open_receivables: number;
  overdue_amount: number;
  sessions_count: number;
  hours_total: number;
  avg_session_value: number;
  avg_hourly_rate: number;
  ytd_net: number;
  delta_revenue_pct: number | null;
  active_clients: number;
  open_invoices_count: number;
}

interface RevenueChartMonth {
  month: string;
  current_year: number;
  previous_year: number;
}

interface RevenueByTypeItem {
  type: string;
  label: string;
  net_amount: number;
  percentage: number;
}

interface InsightItem {
  type: string;
  title: string;
  body: string;
  severity: string;
}

interface InvoiceListItem {
  id: string;
  invoice_number: string;
  client_name: string;
  invoice_date: string;
  due_date: string;
  net_amount: number;
  tax_amount: number;
  gross_amount: number;
  status: string;
}

interface InvoicesData {
  paid_total: number;
  open_total: number;
  overdue_total: number;
  paid_count: number;
  open_count: number;
  overdue_count: number;
  invoices: InvoiceListItem[];
  total: number;
  page: number;
  pages: number;
}

interface ClientRankingItem {
  client_id: string;
  full_name: string;
  initials: string;
  sessions_count: number;
  revenue_net: number;
  revenue_share_pct: number;
  avg_frequency_days: number | null;
  counseling_type: string;
}

interface ClientsStatsData {
  active: number;
  archived: number;
  new_in_period: number;
  pending: number;
}

interface QuarterData {
  quarter: string;
  year: number;
  net_amount: number;
}

interface TaxOverviewData {
  gross_ytd: number;
  net_ytd: number;
  tax_ytd: number;
  is_small_business: boolean;
  next_vat_deadline: string;
  quarters: QuarterData[];
}

type TabKey = 'umsatz' | 'rechnungen' | 'klienten' | 'export';
type PeriodKey = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

// ── Helpers ────────────────────────────────────────────────────

const fmtEur = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d: string) => {
  try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
};

const PERIOD_LABELS: Record<PeriodKey, string> = {
  today: 'Heute', week: 'Woche', month: 'Monat', quarter: 'Quartal', year: 'Jahr', custom: 'Benutzerdefiniert',
};

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Entwurf', cls: 'rpt-badge-info' },
  sent: { label: 'Offen', cls: 'rpt-badge-warning' },
  paid: { label: 'Bezahlt', cls: 'rpt-badge-success' },
  partially_paid: { label: 'Teilweise', cls: 'rpt-badge-warning' },
  overdue: { label: 'Überfällig', cls: 'rpt-badge-danger' },
  cancelled: { label: 'Storniert', cls: 'rpt-badge-muted' },
};

const TYPE_LABELS: Record<string, string> = {
  individual: 'Einzelberatung', couple: 'Paarberatung', family: 'Familienberatung',
  group: 'Gruppenberatung', organizational: 'Organisation',
};

const DONUT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

/** Resolve a CSS variable to its computed value for use in SVG fills. */
const getCssVar = (varName: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const val = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return val || fallback;
};

// ── Component ──────────────────────────────────────────────────

export default function Reports() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>('umsatz');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [compare, setCompare] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);

  // Tab 1: Umsatz & Finanzen
  const [financials, setFinancials] = useState<FinancialsData | null>(null);
  const [chartData, setChartData] = useState<RevenueChartMonth[]>([]);
  const [byType, setByType] = useState<RevenueByTypeItem[]>([]);
  const [insights, setInsights] = useState<InsightItem[]>([]);

  // Tab 2: Rechnungen
  const [invData, setInvData] = useState<InvoicesData | null>(null);
  const [invPage, setInvPage] = useState(1);
  const [invStatusFilter, setInvStatusFilter] = useState('all');

  // Tab 3: Klienten
  const [ranking, setRanking] = useState<ClientRankingItem[]>([]);
  const [clientsStats, setClientsStats] = useState<ClientsStatsData | null>(null);

  // Tab 4: Export & Steuer
  const [taxData, setTaxData] = useState<TaxOverviewData | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const periodParams = useCallback(() => {
    const p: Record<string, string | boolean> = { period, compare };
    if (period === 'custom' && fromDate && toDate) {
      p.date_from = fromDate;
      p.date_to = toDate;
    }
    return p;
  }, [period, compare, fromDate, toDate]);

  // ── Data Fetching ──────────────────────────────────────────

  const loadUmsatz = useCallback(async () => {
    setLoading(true);
    try {
      const [fin, chart, types, ins] = await Promise.all([
        reportsApi.financials(periodParams()),
        reportsApi.revenueChart(periodParams()),
        reportsApi.revenueByType(periodParams()),
        reportsApi.insights(periodParams()),
      ]);
      setFinancials(fin.data);
      setChartData(chart.data);
      setByType(types.data);
      setInsights(ins.data);
    } catch { showToast('Finanzdaten konnten nicht geladen werden', 'error'); }
    setLoading(false);
  }, [periodParams]);

  const loadRechnungen = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await reportsApi.invoices({
        ...periodParams(),
        status_filter: invStatusFilter === 'all' ? undefined : invStatusFilter,
        page: invPage, page_size: 10,
      });
      setInvData(data);
    } catch { showToast('Rechnungsdaten konnten nicht geladen werden', 'error'); }
    setLoading(false);
  }, [periodParams, invPage, invStatusFilter]);

  const loadKlienten = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        reportsApi.clientsRanking(periodParams()),
        reportsApi.clientsStats(periodParams()),
      ]);
      setRanking(r.data);
      setClientsStats(s.data);
    } catch { showToast('Klientendaten konnten nicht geladen werden', 'error'); }
    setLoading(false);
  }, [periodParams]);

  const loadExport = useCallback(async () => {
    if (taxData) return;
    try {
      const { data } = await reportsApi.taxOverview();
      setTaxData(data);
    } catch { /* silently fail for tax overview */ }
  }, [taxData]);

  useEffect(() => {
    if (activeTab === 'umsatz') loadUmsatz();
    else if (activeTab === 'rechnungen') loadRechnungen();
    else if (activeTab === 'klienten') loadKlienten();
    else if (activeTab === 'export') loadExport();
  }, [activeTab, loadUmsatz, loadRechnungen, loadKlienten, loadExport]);

  // ── Export handlers ────────────────────────────────────────

  const handleExport = async (type: string) => {
    setExporting(type);
    try {
      let resp;
      const params = periodParams();
      switch (type) {
        case 'datev': resp = await reportsApi.exportDatev(params); break;
        case 'csv': resp = await reportsApi.exportCsv({ content: 'invoices', ...params }); break;
        case 'pdf': resp = await reportsApi.exportPdf(params); break;
        case 'eur': resp = await reportsApi.exportEur(params); break;
        case 'clients': resp = await reportsApi.exportClients(); break;
        default: return;
      }
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext: Record<string, string> = { datev: 'csv', csv: 'csv', pdf: 'pdf', eur: 'xlsx', clients: 'zip' };
      a.download = `Export_${type}_${new Date().toISOString().split('T')[0]}.${ext[type] || 'bin'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Export erfolgreich', 'success');
    } catch { showToast('Export fehlgeschlagen', 'error'); }
    setExporting(null);
  };

  // ── Period change handler ──────────────────────────────────

  const changePeriod = (p: PeriodKey) => {
    setPeriod(p);
    setInvPage(1);
  };

  // ── Skeleton Loading ───────────────────────────────────────

  const LoadingSkeleton = () => (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <SkeletonTable rows={5} />
    </div>
  );

  // ── Insight Icon Helper ────────────────────────────────────

  const InsightIcon = ({ severity }: { severity: string }) => {
    const map: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
      success: { bg: 'var(--success-bg)', color: 'var(--success-text)', icon: <TrendingUp size={16} /> },
      warning: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', icon: <AlertCircle size={16} /> },
      info: { bg: 'var(--bg-muted)', color: 'var(--primary)', icon: <Lightbulb size={16} /> },
    };
    const m = map[severity] || map.info;
    return (
      <div className="rpt-insight-icon" style={{ background: m.bg, color: m.color }}>
        {m.icon}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <div>
      {/* ── Topbar ──────────────────────────────────────────── */}
      <div className="rpt-topbar">
        <div className="rpt-topbar-left">
          <div className="rpt-topbar-title">Berichte & Auswertungen</div>
          <div className="rpt-topbar-sub">Detailanalyse · Exporte · Steuerübersicht</div>
        </div>
        <div className="rpt-topbar-right">
          <button className="rpt-btn rpt-btn-outline rpt-btn-sm" onClick={() => handleExport('pdf')}
            disabled={exporting === 'pdf'}>
            <Download size={14} />
            <span>{exporting === 'pdf' ? 'Wird erstellt...' : 'PDF-Report'}</span>
          </button>
          <button className="rpt-btn rpt-btn-primary rpt-btn-sm" onClick={() => handleExport('datev')}
            disabled={exporting === 'datev'}>
            <Download size={14} />
            <span>{exporting === 'datev' ? 'Wird erstellt...' : 'DATEV Export'}</span>
          </button>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────── */}
      <div className="rpt-tabs-bar">
        <button className={`rpt-tab ${activeTab === 'umsatz' ? 'active' : ''}`} onClick={() => setActiveTab('umsatz')}>
          <BarChart3 size={14} /> Umsatz & Finanzen
        </button>
        <button className={`rpt-tab ${activeTab === 'rechnungen' ? 'active' : ''}`} onClick={() => setActiveTab('rechnungen')}>
          <FileText size={14} /> Rechnungen
          {invData && <span className="rpt-tab-badge">{invData.total}</span>}
        </button>
        <button className={`rpt-tab ${activeTab === 'klienten' ? 'active' : ''}`} onClick={() => setActiveTab('klienten')}>
          <Users size={14} /> Klienten-Analyse
        </button>
        <button className={`rpt-tab ${activeTab === 'export' ? 'active' : ''}`} onClick={() => setActiveTab('export')}>
          <Download size={14} /> Export & Steuer
        </button>
      </div>

      {/* ── Filter Strip ────────────────────────────────────── */}
      <div className="rpt-filter-strip">
        <div className="rpt-filter-group">
          <span className="rpt-filter-label">Zeitraum:</span>
          {(['today', 'week', 'month', 'quarter', 'year'] as PeriodKey[]).map(p => (
            <button key={p} className={`rpt-period-btn ${period === p ? 'active' : ''}`}
              onClick={() => changePeriod(p)}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
          <button className={`rpt-period-btn ${period === 'custom' ? 'active' : ''}`}
            onClick={() => changePeriod('custom')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={13} /> Benutzerdefiniert
          </button>
          {period === 'custom' && (
            <>
              <input type="date" className="rpt-date-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>bis</span>
              <input type="date" className="rpt-date-input" value={toDate} onChange={e => setToDate(e.target.value)} />
            </>
          )}
        </div>
        <label className="rpt-compare-toggle">
          <input type="checkbox" checked={compare} onChange={e => setCompare(e.target.checked)} />
          <span>Vergleich</span>
        </label>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="rpt-content">

        {loading && <LoadingSkeleton />}

        {/* ▶ TAB 1: UMSATZ & FINANZEN ─────────────────────── */}
        {activeTab === 'umsatz' && !loading && financials && (
          <div className="rpt-panel">

            {/* KPI Row */}
            <div className="rpt-kpi-row">
              {/* Umsatz */}
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-success" />
                <div className="rpt-kpi-icon" style={{ background: 'var(--success-bg)', color: 'var(--success-text)' }}><Euro size={18} /></div>
                <div className="rpt-kpi-lbl">Umsatz (Netto)</div>
                <div className="rpt-kpi-val">{fmtEur(financials.revenue_net)} <small>€</small></div>
                {financials.delta_revenue_pct !== null && (
                  <span className={`rpt-kpi-delta ${financials.delta_revenue_pct >= 0 ? 'rpt-delta-up' : 'rpt-delta-dn'}`}>
                    {financials.delta_revenue_pct >= 0 ? '↑' : '↓'} {Math.abs(financials.delta_revenue_pct).toFixed(1)} %
                  </span>
                )}
              </div>
              {/* Offene Forderungen */}
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-warning" />
                <div className="rpt-kpi-icon" style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)' }}><Clock size={18} /></div>
                <div className="rpt-kpi-lbl">Offene Forderungen</div>
                <div className="rpt-kpi-val">{fmtEur(financials.open_receivables)} <small>€</small></div>
                <span className="rpt-kpi-delta rpt-delta-neu">→ offen</span>
              </div>
              {/* Ø Sitzungswert */}
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-primary" />
                <div className="rpt-kpi-icon" style={{ background: 'var(--bg-muted)', color: 'var(--primary)' }}><Activity size={18} /></div>
                <div className="rpt-kpi-lbl">Ø Sitzungswert</div>
                <div className="rpt-kpi-val">{fmtEur(financials.avg_session_value)} <small>€</small></div>
              </div>
              {/* Überfällig */}
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-danger" />
                <div className="rpt-kpi-icon" style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}><AlertCircle size={18} /></div>
                <div className="rpt-kpi-lbl">Überfällig</div>
                <div className="rpt-kpi-val">{fmtEur(financials.overdue_amount)} <small>€</small></div>
                {financials.overdue_amount === 0 && (
                  <span className="rpt-kpi-delta rpt-delta-up">✓ Alles beglichen</span>
                )}
              </div>
            </div>

            {/* Charts: Umsatzverlauf + Donut */}
            <div className="rpt-grid-3">
              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div>
                    <div className="rpt-card-title">Umsatzverlauf {new Date().getFullYear()}</div>
                    <div className="rpt-card-sub">Monatlicher Umsatz vs. Vorjahr</div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11.5px', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--primary)' }} />{new Date().getFullYear()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11.5px', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--border)' }} />{new Date().getFullYear() - 1}
                    </div>
                  </div>
                </div>
                <div className="rpt-card-body">
                  {chartData.length > 0 && (
                    <BarChart
                      data={chartData.map(m => ({
                        label: m.month,
                        values: [m.current_year, m.previous_year],
                      }))}
                      legend={[String(new Date().getFullYear()), String(new Date().getFullYear() - 1)]}
                      colors={[getCssVar('--primary', '#8B6F47'), getCssVar('--border', '#E5D9C8')]}
                    />
                  )}
                </div>
              </div>

              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div>
                    <div className="rpt-card-title">Nach Beratungstyp</div>
                    <div className="rpt-card-sub">Anteil am Gesamtumsatz</div>
                  </div>
                </div>
                <div className="rpt-card-body">
                  {byType.length > 0 && (
                    <DonutChart
                      data={byType.map(t => ({ label: t.label, value: t.net_amount }))}
                      colors={DONUT_COLORS}
                      size={200}
                      centerLabel={`${fmtEur(byType.reduce((s, t) => s + t.net_amount, 0))} €`}
                      centerSub="Netto"
                      formatValue={(v) => `${fmtEur(v)} €`}
                    />
                  )}
                  {byType.length === 0 && (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      Keine Daten vorhanden
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Highlight Row */}
            <div className="rpt-highlight-row">
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--success-bg)', color: 'var(--success-text)' }}><Calendar size={17} /></div>
                <div><div className="rpt-hl-lbl">Sitzungen</div><div className="rpt-hl-val">{financials.sessions_count}</div></div>
              </div>
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--bg-muted)', color: 'var(--primary)' }}><Clock size={17} /></div>
                <div><div className="rpt-hl-lbl">Beratungsstunden</div><div className="rpt-hl-val">{financials.hours_total.toFixed(1)} Std.</div></div>
              </div>
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--bg-muted)', color: 'var(--primary)' }}><DollarSign size={17} /></div>
                <div><div className="rpt-hl-lbl">Ø Stundensatz</div><div className="rpt-hl-val">{fmtEur(financials.avg_hourly_rate)} €</div></div>
              </div>
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--success-bg)', color: 'var(--success-text)' }}><TrendingUp size={17} /></div>
                <div><div className="rpt-hl-lbl">Jahresumsatz (YTD)</div><div className="rpt-hl-val">{fmtEur(financials.ytd_net)} €</div></div>
              </div>
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)' }}><Users size={17} /></div>
                <div><div className="rpt-hl-lbl">Aktive Klienten</div><div className="rpt-hl-val">{financials.active_clients}</div></div>
              </div>
              <div className="rpt-hl-box">
                <div className="rpt-hl-icon" style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}><FileText size={17} /></div>
                <div><div className="rpt-hl-lbl">Offene Rechnungen</div><div className="rpt-hl-val">{financials.open_invoices_count}</div></div>
              </div>
            </div>

            {/* Insights */}
            {insights.length > 0 && (
              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div className="rpt-card-title">Automatische Insights</div>
                  <span className="rpt-badge rpt-badge-primary">Analyse</span>
                </div>
                <div className="rpt-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {insights.map((ins, i) => (
                    <div key={i} className="rpt-insight">
                      <InsightIcon severity={ins.severity} />
                      <div>
                        <div className="rpt-insight-title">{ins.title}</div>
                        <div className="rpt-insight-body">{ins.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ▶ TAB 2: RECHNUNGEN ─────────────────────────────── */}
        {activeTab === 'rechnungen' && !loading && invData && (
          <div className="rpt-panel">

            {/* KPI Row (3 cards) */}
            <div className="rpt-kpi-row-3">
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-success" />
                <div className="rpt-kpi-lbl">Bezahlt</div>
                <div className="rpt-kpi-val">{fmtEur(invData.paid_total)} <small>€</small></div>
                <span className="rpt-kpi-delta rpt-delta-up">{invData.paid_count} Rechnungen</span>
              </div>
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-warning" />
                <div className="rpt-kpi-lbl">Offen / Ausstehend</div>
                <div className="rpt-kpi-val">{fmtEur(invData.open_total)} <small>€</small></div>
                <span className="rpt-kpi-delta rpt-delta-neu">{invData.open_count} Rechnungen</span>
              </div>
              <div className="rpt-kpi">
                <div className="rpt-kpi-stripe rpt-kpi-stripe-danger" />
                <div className="rpt-kpi-lbl">Überfällig</div>
                <div className="rpt-kpi-val">{fmtEur(invData.overdue_total)} <small>€</small></div>
                {invData.overdue_total === 0 ? (
                  <span className="rpt-kpi-delta rpt-delta-up">✓ Keine</span>
                ) : (
                  <span className="rpt-kpi-delta rpt-delta-dn">{invData.overdue_count} Rechnungen</span>
                )}
              </div>
            </div>

            {/* Invoice Table */}
            <div className="rpt-card">
              <div className="rpt-card-hd">
                <div>
                  <div className="rpt-card-title">Alle Rechnungen</div>
                  <div className="rpt-card-sub">{invData.total} gesamt</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="rpt-select" value={invStatusFilter} onChange={e => { setInvStatusFilter(e.target.value); setInvPage(1); }}>
                    <option value="all">Alle Status</option>
                    <option value="sent">Offen</option>
                    <option value="paid">Bezahlt</option>
                    <option value="overdue">Überfällig</option>
                    <option value="draft">Entwurf</option>
                  </select>
                  <button className="rpt-btn rpt-btn-outline rpt-btn-sm" onClick={() => handleExport('csv')}
                    disabled={exporting === 'csv'}>
                    <Download size={14} /> CSV
                  </button>
                </div>
              </div>
              <div className="rpt-table-wrap">
                <table className="rpt-table">
                  <thead>
                    <tr>
                      <th>Rechnungs-Nr.</th>
                      <th>Klient</th>
                      <th>Datum</th>
                      <th>Fällig am</th>
                      <th>Betrag</th>
                      <th>MwSt.</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invData.invoices.map(inv => (
                      <tr key={inv.id}>
                        <td className="rpt-td-muted">{inv.invoice_number}</td>
                        <td>{inv.client_name}</td>
                        <td className="rpt-td-muted">{fmtDate(inv.invoice_date)}</td>
                        <td className="rpt-td-muted">{fmtDate(inv.due_date)}</td>
                        <td className="rpt-td-strong">{fmtEur(inv.gross_amount)} €</td>
                        <td className="rpt-td-muted">{fmtEur(inv.tax_amount)} €</td>
                        <td>
                          <span className={`rpt-badge ${STATUS_MAP[inv.status]?.cls || 'rpt-badge-muted'}`}>
                            {STATUS_MAP[inv.status]?.label || inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {invData.invoices.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                        Keine Rechnungen gefunden
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {invData.pages > 1 && (
                <div className="rpt-table-foot">
                  <span>Seite {invData.page} von {invData.pages} · {invData.total} Rechnungen</span>
                  <div className="rpt-pager">
                    {Array.from({ length: Math.min(invData.pages, 5) }, (_, i) => i + 1).map(p => (
                      <button key={p} className={`rpt-pg ${invData.page === p ? 'on' : ''}`}
                        onClick={() => setInvPage(p)}>{p}</button>
                    ))}
                    {invData.pages > 5 && (
                      <button className="rpt-pg" onClick={() => setInvPage(Math.min(invData.page + 1, invData.pages))}>›</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ▶ TAB 3: KLIENTEN-ANALYSE ──────────────────────── */}
        {activeTab === 'klienten' && !loading && (
          <div className="rpt-panel">
            <div className="rpt-grid-2">

              {/* Left: Ranking */}
              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div>
                    <div className="rpt-card-title">Umsatz-Ranking</div>
                    <div className="rpt-card-sub">Top Klienten nach Umsatz</div>
                  </div>
                  {clientsStats && <span className="rpt-badge rpt-badge-muted">{clientsStats.active} aktiv</span>}
                </div>
                <div>
                  {ranking.length === 0 && (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      Keine Daten vorhanden
                    </div>
                  )}
                  {ranking.map((client, i) => {
                    const maxRev = ranking[0]?.revenue_net || 1;
                    const barPct = Math.round((client.revenue_net / maxRev) * 100);
                    return (
                      <div key={client.client_id} className="rpt-ranking-item">
                        <div className="rpt-rank-num" style={{ background: i < 2 ? 'var(--primary)' : 'var(--bg-muted)', color: i < 2 ? '#fff' : 'var(--primary)' }}>
                          {i + 1}
                        </div>
                        <div className="rpt-rank-avatar">{client.initials}</div>
                        <div className="rpt-rank-info">
                          <div className="rpt-rank-name">{client.full_name}</div>
                          <div className="rpt-rank-meta">
                            {client.sessions_count} Sitzungen · {TYPE_LABELS[client.counseling_type] || client.counseling_type}
                          </div>
                          <div className="rpt-mini-bar-wrap" style={{ marginTop: 5 }}>
                            <div className="rpt-mini-bar" style={{ width: `${barPct}%` }} />
                          </div>
                        </div>
                        <div className="rpt-rank-amount">
                          <strong>{fmtEur(client.revenue_net)} €</strong>
                          <div className="rpt-rank-pct">{client.revenue_share_pct.toFixed(1)} %</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Frequency + Status */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Frequency */}
                <div className="rpt-card">
                  <div className="rpt-card-hd">
                    <div>
                      <div className="rpt-card-title">Sitzungsfrequenz</div>
                      <div className="rpt-card-sub">Ø Tage zwischen Sitzungen</div>
                    </div>
                  </div>
                  <div className="rpt-card-body">
                    {ranking.filter(c => c.avg_frequency_days !== null).slice(0, 5).map(client => {
                      const freq = client.avg_frequency_days || 30;
                      const barPct = Math.max(10, Math.min(100, Math.round((1 - freq / 40) * 100)));
                      const barColor = freq <= 10 ? 'var(--primary)' : freq <= 20 ? '#B8860B' : '#D4C5B0';
                      const badgeCls = freq <= 10 ? 'rpt-badge-success' : freq <= 20 ? 'rpt-badge-warning' : 'rpt-badge-muted';
                      return (
                        <div key={client.client_id} className="rpt-freq-item">
                          <span style={{ color: 'var(--text-muted)' }}>{client.full_name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="rpt-freq-bar-wrap">
                              <div className="rpt-freq-bar" style={{ width: `${barPct}%`, background: barColor }} />
                            </div>
                            <span className={`rpt-badge ${badgeCls}`}>Ø {Math.round(freq)} Tage</span>
                          </div>
                        </div>
                      );
                    })}
                    {ranking.filter(c => c.avg_frequency_days !== null).length === 0 && (
                      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                        Keine Frequenzdaten
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Status */}
                {clientsStats && (
                  <div className="rpt-card">
                    <div className="rpt-card-hd">
                      <div className="rpt-card-title">Klienten-Status</div>
                    </div>
                    <div className="rpt-card-body">
                      <div className="rpt-status-grid">
                        <div className="rpt-status-box" style={{ background: 'var(--success-bg)' }}>
                          <div className="rpt-status-val" style={{ color: 'var(--success-text)' }}>{clientsStats.active}</div>
                          <div className="rpt-status-label" style={{ color: 'var(--success-text)' }}>Aktiv</div>
                        </div>
                        <div className="rpt-status-box" style={{ background: 'var(--bg-muted)' }}>
                          <div className="rpt-status-val" style={{ color: 'var(--text-muted)' }}>{clientsStats.archived}</div>
                          <div className="rpt-status-label" style={{ color: 'var(--text-muted)' }}>Archiviert</div>
                        </div>
                        <div className="rpt-status-box" style={{ background: 'var(--warning-bg)' }}>
                          <div className="rpt-status-val" style={{ color: 'var(--warning-text)' }}>{clientsStats.new_in_period}</div>
                          <div className="rpt-status-label" style={{ color: 'var(--warning-text)' }}>Neu (Zeitraum)</div>
                        </div>
                        <div className="rpt-status-box" style={{ background: 'var(--info-bg)' }}>
                          <div className="rpt-status-val" style={{ color: 'var(--info-text)' }}>{clientsStats.pending}</div>
                          <div className="rpt-status-label" style={{ color: 'var(--info-text)' }}>Wartend</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ▶ TAB 4: EXPORT & STEUER ────────────────────────── */}
        {activeTab === 'export' && !loading && (
          <div className="rpt-panel">
            <div className="rpt-grid-2">

              {/* Steuerübersicht */}
              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div>
                    <div className="rpt-card-title">Steuerübersicht {new Date().getFullYear()}</div>
                    <div className="rpt-card-sub">EÜR-relevante Kennzahlen</div>
                  </div>
                  <span className="rpt-badge rpt-badge-warning">Kein Steuerberater</span>
                </div>
                <div className="rpt-card-body">
                  {taxData ? (
                    <>
                      <div className="rpt-steuer-row">
                        <span className="rpt-steuer-label">Bruttoumsatz (YTD)</span>
                        <span className="rpt-steuer-value">{fmtEur(taxData.gross_ytd)} €</span>
                      </div>
                      <div className="rpt-steuer-row">
                        <span className="rpt-steuer-label">Nettoumsatz (YTD)</span>
                        <span className="rpt-steuer-value">{fmtEur(taxData.net_ytd)} €</span>
                      </div>
                      <div className="rpt-steuer-row">
                        <span className="rpt-steuer-label">MwSt. ausgewiesen</span>
                        <span className="rpt-steuer-value" style={{ color: 'var(--warning-text)' }}>{fmtEur(taxData.tax_ytd)} €</span>
                      </div>
                      <div className="rpt-steuer-row">
                        <span className="rpt-steuer-label">Kleinunternehmerregelung</span>
                        <span className={`rpt-badge ${taxData.is_small_business ? 'rpt-badge-success' : 'rpt-badge-info'}`}>
                          {taxData.is_small_business ? 'Möglich' : 'Prüfen'}
                        </span>
                      </div>
                      <div className="rpt-steuer-row">
                        <span className="rpt-steuer-label">Nächste USt-Voranmeldung</span>
                        <span className="rpt-steuer-value" style={{ color: 'var(--danger-text)' }}>{taxData.next_vat_deadline}</span>
                      </div>
                      <div className="rpt-warning-box">
                        Diese Übersicht ersetzt keine steuerliche Beratung. Bitte prüfen Sie alle Angaben mit Ihrem Steuerberater.
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                      Steuerdaten werden geladen...
                    </div>
                  )}
                </div>
              </div>

              {/* Jahresvergleich */}
              <div className="rpt-card">
                <div className="rpt-card-hd">
                  <div>
                    <div className="rpt-card-title">Jahresvergleich</div>
                    <div className="rpt-card-sub">Umsatzentwicklung pro Quartal</div>
                  </div>
                </div>
                <div className="rpt-card-body">
                  {taxData && taxData.quarters.length > 0 ? (
                    <>
                      {(() => {
                        const maxQ = Math.max(...taxData.quarters.map(q => q.net_amount), 1);
                        const currentYear = new Date().getFullYear();
                        const currentQuarters = taxData.quarters.filter(q => q.year === currentYear);
                        const prevQuarters = taxData.quarters.filter(q => q.year === currentYear - 1);
                        return (
                          <>
                            {currentQuarters.map(q => (
                              <div key={`${q.quarter}-${q.year}`} className="rpt-quarter-item">
                                <div className="rpt-quarter-header">
                                  <span>{q.quarter} {q.year}</span>
                                  <strong style={{ color: 'var(--text)' }}>{fmtEur(q.net_amount)} €</strong>
                                </div>
                                <div className="rpt-quarter-bar">
                                  <div className="rpt-quarter-fill" style={{ width: `${(q.net_amount / maxQ) * 100}%`, background: 'var(--primary)' }} />
                                </div>
                              </div>
                            ))}
                            {prevQuarters.length > 0 && (
                              <>
                                <div className="rpt-sep" />
                                {prevQuarters.map(q => (
                                  <div key={`${q.quarter}-${q.year}`} className="rpt-quarter-item">
                                    <div className="rpt-quarter-header">
                                      <span>{q.quarter} {q.year}</span>
                                      <strong style={{ color: 'var(--text)' }}>{fmtEur(q.net_amount)} €</strong>
                                    </div>
                                    <div className="rpt-quarter-bar">
                                      <div className="rpt-quarter-fill" style={{ width: `${(q.net_amount / maxQ) * 100}%`, background: 'var(--border)' }} />
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                            {currentQuarters.length > 0 && prevQuarters.length > 0 && (() => {
                              const curQ1 = currentQuarters.find(q => q.quarter === 'Q1');
                              const prevQ1 = prevQuarters.find(q => q.quarter === 'Q1');
                              if (curQ1 && prevQ1 && prevQ1.net_amount > 0) {
                                const pct = ((curQ1.net_amount - prevQ1.net_amount) / prevQ1.net_amount * 100).toFixed(1);
                                return (
                                  <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--success-bg)', borderRadius: 8, fontSize: 12, color: 'var(--success-text)' }}>
                                    ↑ Q1 {currentYear} liegt {pct} % über Q1 {currentYear - 1}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                      Keine Quartalsdaten
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Export Options Grid */}
            <div className="rpt-card" style={{ marginTop: 16 }}>
              <div className="rpt-card-hd">
                <div>
                  <div className="rpt-card-title">Exportoptionen</div>
                  <div className="rpt-card-sub">Daten für Steuerberater, Buchhaltung oder eigene Ablage</div>
                </div>
              </div>
              <div className="rpt-card-body">
                <div className="rpt-export-grid">
                  <div className="rpt-export-box" onClick={() => handleExport('datev')}>
                    <div className="rpt-export-icon"><FileText size={20} /></div>
                    <div>
                      <div className="rpt-export-name">DATEV-Export</div>
                      <div className="rpt-export-desc">Buchungsstapel für Steuerberater (CSV)</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                  <div className="rpt-export-box" onClick={() => handleExport('csv')}>
                    <div className="rpt-export-icon"><FileSpreadsheet size={20} /></div>
                    <div>
                      <div className="rpt-export-name">Excel / CSV Export</div>
                      <div className="rpt-export-desc">Alle Rechnungen als Tabelle</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                  <div className="rpt-export-box" onClick={() => handleExport('pdf')}>
                    <div className="rpt-export-icon"><Download size={20} /></div>
                    <div>
                      <div className="rpt-export-name">Monatsbericht PDF</div>
                      <div className="rpt-export-desc">Zusammenfassung als druckfähiges PDF</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                  <div className="rpt-export-box" onClick={() => handleExport('eur')}>
                    <div className="rpt-export-icon"><PieChart size={20} /></div>
                    <div>
                      <div className="rpt-export-name">EÜR-Vorlage</div>
                      <div className="rpt-export-desc">Einnahmen-Überschuss-Rechnung (Excel)</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                  <div className="rpt-export-box" onClick={() => handleExport('clients')}>
                    <div className="rpt-export-icon"><Users size={20} /></div>
                    <div>
                      <div className="rpt-export-name">Klienten-Datenexport</div>
                      <div className="rpt-export-desc">Alle Klientendaten (DSGVO-konform, ZIP)</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                  <div className="rpt-export-box" onClick={() => showToast('Tätigkeitsbericht wird in einem zukünftigen Update verfügbar', 'info')}>
                    <div className="rpt-export-icon"><Activity size={20} /></div>
                    <div>
                      <div className="rpt-export-name">Tätigkeitsbericht</div>
                      <div className="rpt-export-desc">Stunden & Leistungen je Klient (PDF)</div>
                    </div>
                    <div className="rpt-export-arrow"><ChevronRight size={16} /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
